import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Trophy, Sparkles, Calendar } from "lucide-react";
import MatchCard from "@/components/MatchCard";
import StandingsTable from "@/components/StandingsTable";
import AdSlot from "@/components/AdSlot";
import { fetchMatches, fetchStandings, fetchOverview, fetchScorers } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const HERO_IMG = "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3ODI0MDU1NDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useI18n();
  const [live, setLive] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [finished, setFinished] = useState([]);
  const [standings, setStandings] = useState([]);
  const [overview, setOverview] = useState({});
  const [scorers, setScorers] = useState([]);
  const [standingsLeague, setStandingsLeague] = useState({});
  const [scorersLeague, setScorersLeague] = useState({});

  useEffect(() => {
    const loadAll = async () => {
      const [l, u, f, s, o, sc] = await Promise.all([
        fetchMatches({ status: "live", limit: 100 }),
        fetchMatches({ status: "upcoming", limit: 6 }),
        fetchMatches({ status: "finished", limit: 4 }),
        fetchStandings("world-cup"),
        fetchOverview(),
        fetchScorers("world-cup"),
      ]);
      setLive(l);
      setUpcoming(u);
      setFinished(f);
      setStandings(s.standings || []);
      setStandingsLeague(s.league || {});
      setOverview(o);
      setScorers(sc.scorers || []);
      setScorersLeague(sc.league || {});
    };
    loadAll();
    // Refresh live matches + overview every 30 seconds
    const refreshLive = async () => {
      try {
        const [l, o] = await Promise.all([
          fetchMatches({ status: "live", limit: 100 }),
          fetchOverview(),
        ]);
        setLive(l);
        setOverview(o);
      } catch (e) { /* silent */ }
    };
    const timer = setInterval(refreshLive, 30000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Stadium at night" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
          <div className="absolute inset-0 bg-grid opacity-30" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-volt/40 bg-volt/5 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-volt" />
              <span className="text-xs uppercase tracking-[0.22em] text-volt font-medium">{t("hero.badge")}</span>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.85] uppercase tracking-tighter">
              {t("hero.title_1")}<br />
              {t("hero.title_2")}<br />
              <span className="text-volt">{t("hero.title_3")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl">{t("hero.description")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/matches" data-testid="hero-cta-matches"
                className="inline-flex items-center gap-2 bg-volt text-black font-bold uppercase tracking-[0.18em] px-6 py-3 text-sm hover:bg-[#b4e500] transition-colors">
                {t("hero.cta_matches")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/predictions" data-testid="hero-cta-predictions"
                className="inline-flex items-center gap-2 border border-white/25 text-white uppercase tracking-[0.18em] px-6 py-3 text-sm hover:border-white/60 transition-colors">
                {t("hero.cta_predictions")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 grid grid-cols-2 gap-3">
            <StatTile icon={<Activity />} value={overview.live_count ?? "—"} label={t("hero.stat_live")} highlight />
            <StatTile icon={<Calendar />} value={overview.upcoming_count ?? "—"} label={t("hero.stat_upcoming")} />
            <StatTile icon={<Trophy />} value={overview.leagues_count ?? "—"} label={t("hero.stat_leagues")} />
            <StatTile icon={<Sparkles />} value="AI" label={t("hero.stat_predictions")} />
          </div>
        </div>
      </section>

      {live.length > 0 && (
        <section data-testid="live-section" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <SectionHeader title={t("section.live_now")} subtitle={t("section.live_subtitle", { n: live.length })} live />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
            {live.map((m) => <MatchCard key={m.id} match={m} />)}
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between">
          <SectionHeader title={t("section.upcoming")} subtitle={t("section.upcoming_subtitle")} />
          <Link to="/matches" data-testid="upcoming-view-all"
            className="text-xs uppercase tracking-[0.18em] text-volt hover:underline">
            {t("section.view_all")} →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {upcoming.map((m) => <MatchCard key={m.id} match={m} />)}
        </div>
      </section>

      {/* AD SLOT — between sections */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AdSlot
          testId="ad-home-mid"
          slot=""
          format="auto"
          className="w-full"
          style={{ minHeight: 90 }}
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <SectionHeader title={t("section.results")} subtitle={t("section.results_subtitle")} />
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {finished.map((m) => <MatchCard key={m.id} match={m} compact />)}
          </div>
        </div>

        <div>
          <SectionHeader title={t("section.topscorers")} subtitle={scorersLeague.name || "—"} />
          <div className="mt-6 border border-white/10 divide-y divide-white/5" data-testid="top-scorers-list">
            {scorers.slice(0, 5).map((s) => (
              <div key={s.player} className="flex items-center gap-3 p-3 hover:bg-white/[0.03]">
                <span className="font-display text-xl text-volt w-6 tabular-nums">{s.rank}</span>
                <div className="w-9 h-9 bg-white/5 border border-white/10 flex items-center justify-center font-mono text-[10px]">
                  {s.team.logo}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{s.player}</div>
                  <div className="text-xs text-white/40 truncate">{s.team.name}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl tabular-nums">{s.goals}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{t("section.topscorers")}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between">
          <SectionHeader title={`${standingsLeague.name || "—"} ${t("section.stand_suffix")}`} subtitle={t("section.top_n", { n: 8 })} />
          <Link to="/standings" data-testid="standings-view-all"
            className="text-xs uppercase tracking-[0.18em] text-volt hover:underline">
            {t("section.view_all_standings")} →
          </Link>
        </div>
        <div className="mt-6">
          <StandingsTable rows={standings.slice(0, 8)} testId="home-standings-table" />
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ title, subtitle, live }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        {live && <span className="live-dot" />}
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter">{title}</h2>
      </div>
      <p className="text-xs uppercase tracking-[0.22em] text-white/40 mt-1">{subtitle}</p>
    </div>
  );
}

function StatTile({ icon, value, label, highlight }) {
  return (
    <div className={`border p-4 ${highlight ? "border-volt/40 bg-volt/5" : "border-white/10 bg-white/[0.02]"}`}>
      <div className={`w-8 h-8 flex items-center justify-center ${highlight ? "text-volt" : "text-white/60"}`}>
        {React.cloneElement(icon, { className: "w-5 h-5" })}
      </div>
      <div className="font-display text-4xl mt-2 tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{label}</div>
    </div>
  );
}
